/* Founding Member Preorder — $45 Stripe Checkout = first month of the $45/month membership. */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Crown, Check, ArrowRight } from "lucide-react";

export default function PreorderCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/preorder").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const sid = params.get("preorder_session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/preorder/status/${sid}`);
        if (data.status === "paid" || tries++ > 5) { load(); params.delete("preorder_session_id"); setParams(params, { replace: true }); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, setParams]);

  const checkout = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/preorder/checkout"); window.location.href = data.url; }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  if (!info) return null;
  const paid = info.preorder?.status === "paid";
  return (
    <div className={`rounded-2xl p-6 sm:p-7 mb-6 border ${paid ? "bg-forest text-white border-forest" : "bg-ink text-cream border-ink"}`} data-testid="preorder-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold"><Crown className="w-3 h-3" /> Withdraw Guard — Founding Member Preorder</div>
          {paid ? (
            <>
              <h2 className="font-serif text-2xl sm:text-3xl mt-2" data-testid="preorder-paid">You&apos;re a Founding Member.</h2>
              <p className="text-sm text-mint-mid mt-2">$45 reserved on {new Date(info.preorder.paid_at).toLocaleDateString()} · applies to your first month · no further charge until your membership begins.</p>
            </>
          ) : (
            <>
              <h2 className="font-serif text-2xl sm:text-3xl mt-2"><span className="font-mono">$45</span> today · your first month of $45/month</h2>
              <ul className="mt-3 space-y-1.5 text-sm text-sand">
                {["$45 = your first month · membership is $45/month, nothing else", "No additional charge until your membership begins", "Cancel / refund according to the stated preorder terms"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {t}</li>
                ))}
              </ul>
              <details className="mt-3 text-xs text-sage-light" data-testid="preorder-terms">
                <summary className="cursor-pointer select-none underline underline-offset-2">Preorder terms</summary>
                <ul className="mt-2 list-disc pl-5 space-y-1">{info.terms.map((t) => <li key={t}>{t}</li>)}</ul>
              </details>
            </>
          )}
        </div>
        {!paid && (
          <div className="shrink-0 text-right">
            <div className="font-mono text-4xl">$45<span className="text-base text-sage">/mo</span></div>
            <div className="text-[10px] uppercase tracking-wider text-sage">today · card via Stripe</div>
            <button onClick={checkout} disabled={busy}
              className="mt-3 inline-flex items-center gap-2 bg-gold text-white rounded-lg px-5 py-3 text-sm font-medium hover:bg-gold-deep disabled:opacity-50 transition-colors"
              data-testid="preorder-checkout-btn">
              {busy ? "Opening checkout…" : "Preorder for $45"} <ArrowRight className="w-4 h-4" />
            </button>
            {err && <div className="text-xs text-[#F4C7BC] mt-2" data-testid="preorder-error">{err}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
